import React, { useEffect, useState, useMemo } from 'react'
import AudioPlayer from './AudioPlayer'
import IssueSolutionMeta from './solutions/IssueSolutionMeta'
import IssueSolutionLabel from './solutions/IssueSolutionLabel'
import { ReactComponent as QuestionMark } from '../svg/normalSvgs/QuestionMark.svg'
import { ReactComponent as LikeIcon } from '../svg/normalSvgs/LikeIcon.svg'
import { ReactComponent as Dislike } from '../svg/normalSvgs/Dislike.svg'
import { useUser } from '../context/user'
import { fileSystemUrl } from '../constants/Connections'
import '../styles/IssueSolution.css'
import { calcReadingTime, milisToMinutesOrSeconds, pluralize } from '../utils/util'
import { solutionLike, solutionDislike } from '../server/server'
import { useTranslation } from 'react-i18next'
import i18n from '../i18n'

export default function IssueSolution({ solution, pressLike, pressDislike, issueTopic }) {
    const { t } = useTranslation()
    const [soundStatus, setSoundStatus] = useState('idle')
    const [currentSoundTime, setCurrentSoundTime] = useState(0)
    const [userLikes, setUserLikes] = useState(solution?.userLikes || [])
    const [userDislikes, setUserDislikes] = useState(solution?.userDislikes || [])
    const hasSound = solution.audio_file
    const soundDuration = hasSound && solution.audio_file.duration
    const audioBarsHeights = hasSound && solution.audio_file.audio_bars_heights
    const hasLabel = solution.expert_recommendation
    const locale = i18n.language
    const estimatedTime = soundDuration ? milisToMinutesOrSeconds(soundDuration) : `${calcReadingTime(solution.text_body)} ${pluralize('minute')} ${t('read')}`
    const numOfLikes = solution.likes
    const { user } = useUser()
    const soundFileUrl = hasSound && `${fileSystemUrl}/${solution.audio_file.storage_name}?token=${user.token}`
    const sound = useMemo(() => {
        if (!hasSound) return

        return new Audio(soundFileUrl)
    }, [soundFileUrl, hasSound])

    const playSound = () => {
        sound.play()
        setSoundStatus('playing')
    }
    const pauseSound = () => {
        sound.pause()
        setSoundStatus('paused')
    }

    const handleLike = async () => {
        var index = userDislikes.indexOf(user.id)
        if (index !== -1) {
            setUserDislikes((t) => [...userDislikes.filter((e) => e !== user.id)])
        }
        setUserLikes((t) => [...t, user.id])
        const data = {
            userId: user.id,
            solution,
            issue: issueTopic,
        }
        await solutionLike(data)
        pressLike(solution)
    }

    const handleDislike = async () => {
        var index = userLikes.indexOf(user.id)
        if (index !== -1) {
            setUserLikes((t) => [...userLikes.filter((e) => e !== user.id)])
        }
        setUserDislikes((t) => [...t, user.id])
        const data = {
            userId: user.id,
            solution,
            issue: issueTopic,
        }
        await solutionDislike(data)
        pressDislike(solution)
    }

    useEffect(() => {
        if (!sound) return

        sound.addEventListener('ended', function (e) {
            setSoundStatus('finished')
        })
        sound.addEventListener('timeupdate', function (e) {
            setCurrentSoundTime(e.target.currentTime)
        })
    }, [sound])

    useEffect(() => {
        return () => {
            if (!sound) return

            sound.pause()
            sound.currentTime = 0
            setSoundStatus('idle')
            setCurrentSoundTime(0)
        }
    }, [solution])

    const liked = solution.userLikes?.includes(user.id)
    const disliked = solution.userDislikes?.includes(user.id)
    return (
        <div className="issue-solution-full-container">
            {hasLabel && (
                <div className="mb-14">
                    <IssueSolutionLabel />
                </div>
            )}

            <div className="issue-solution-full-title">{solution.title}</div>

            <div className="issue-solution-full-meta">
                <IssueSolutionMeta estimatedTime={estimatedTime} numOfLikes={numOfLikes} hasSound={!!soundDuration} liked={liked} />
            </div>

            {hasSound && audioBarsHeights && locale === 'en' && (
                <div className="inline-block mt-20">
                    <AudioPlayer
                        audioBars={audioBarsHeights}
                        playSound={playSound}
                        pauseSound={pauseSound}
                        soundStatus={soundStatus}
                        duration={soundDuration}
                        currentTime={currentSoundTime}
                    />
                </div>
            )}

            <div>
                <pre className="issue-solution-full-text">{solution.text_body}</pre>
            </div>
            <div className="issue-solution-question mt-58">
                <QuestionMark />

                <div className="issue-solution-question-text">{t('wasThisSolutionHelpful')}</div>

                <div className="issue-solution-question-btns">
                    <div className={`issue-solution-question-btn ${liked ? 'liked-press' : ''}`} onClick={handleLike}>
                        <LikeIcon color={liked ? '#7ec5c6' : '#153c4f'} />

                        <div className={`issue-solution-like-text ${liked ? 'liked-press' : ''}`}>{t('yesThanks')}</div>
                    </div>
                    <div className={`issue-solution-question-btn ${disliked ? 'disliked-press' : ''}`} onClick={handleDislike}>
                        <Dislike color={disliked ? '#FFF' : '#153c4f'} />

                        <div className={`issue-solution-like-text ${disliked ? 'disliked-press' : ''}`}>{t('notReally')}</div>
                    </div>
                </div>
            </div>
        </div>
    )
}
